import React, { useCallback, useMemo, memo, useState, useEffect, useRef } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import {
    Counter,
    LeftArrowContainer,
    RightArrowContainer,
    StyledImage,
    StyledImageContainer,
    StyledCarouselWrapper,
    BgPictureContainer,
    EmblaContainer,
    EmblaSlide,
    EmblaViewport,
    VirtualTourWrapper,
} from './styles'
import type { GalleryProps } from './types'
import { LeftNavigator, RightNavigator } from '../icons'
import CarouselDots from './carousel-dots'
import OverlayModal from './overlay-modal'
import { useCarousel } from './hooks/use-carousel'
import FullScreenModal from './full-screen-modal'
import { getImageUrl } from './get-image-url'
import VirtualTourTag from '../../../common/virtual-tour-tag'
import { VirtualTourPosition } from '../../constants'
import { pageTypeEventCategories } from '../../../../services/analytics-service/categories'

const GalleryCarousel: React.FC<GalleryProps & { parentRef: any }> = memo(
    ({
        images,
        video,
        skipFullScreen = false,
        skipOverlay = false,
        imageBorder,
        imageBorderRadius,
        onSlideChange,
        parentRef,
        showDots,
        showCountTile,
        showArrows,
        footer,
        header,
        autoPlay,
        onGalleryImageClick,
        onFullscreenImageClick,
        onFullScreenOpen,
        onOverlayOpen,
        layout,
    }) => {
        const [isFullScreen, setFullScreen] = useState(false)
        const [isOverlayOpen, setOverlay] = useState(false)
        const [shouldAutoplayVideo, setShouldAutoplayVideo] = useState(false)
        const isDraggingRef = useRef(false)
        const dragStartPos = useRef({ x: 0, y: 0 })

        const [emblaRef, emblaApi] = useEmblaCarousel(
            { loop: true, align: 'start' },
            autoPlay ? [Autoplay({ delay: 3000, stopOnInteraction: true })] : [],
        )

        const { currentSlide, prev, next, goToSlide, resumeAutoplay, stopAutoplay } = useCarousel(emblaApi, parentRef, {
            onSlideChange,
        })

        useEffect(() => {
            if (!autoPlay) {
                return
            }
            if (isOverlayOpen || isFullScreen) {
                stopAutoplay()
            } else {
                resumeAutoplay()
            }
        }, [isOverlayOpen, isFullScreen, autoPlay, stopAutoplay, resumeAutoplay])

        const handleOnItemClick = useCallback(() => {
            // Track image click
            onGalleryImageClick?.({
                imageIndex: currentSlide,
                layout: layout!,
            })

            // Autoplay video only if clicking on index 0 (video position)
            setShouldAutoplayVideo(currentSlide === 0)

            if (!skipFullScreen) {
                setFullScreen(true)
            } else if (!skipOverlay) {
                setOverlay(true)
            }
        }, [skipFullScreen, skipOverlay, onGalleryImageClick, currentSlide, layout])

        const handleMouseDown = useCallback((event: React.MouseEvent) => {
            dragStartPos.current = { x: event.clientX, y: event.clientY }
            isDraggingRef.current = false
        }, [])

        const handleMouseMove = useCallback((event: React.MouseEvent) => {
            const dx = event.clientX - dragStartPos.current.x
            const dy = event.clientY - dragStartPos.current.y
            if (!isDraggingRef.current && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
                isDraggingRef.current = true
            }
        }, [])

        const handleMouseEnter = useCallback(() => {
            stopAutoplay()
        }, [stopAutoplay])

        const handleMouseLeave = useCallback(() => {
            resumeAutoplay()
        }, [resumeAutoplay])

        const handleMouseUp = useCallback(() => {
            if (!isDraggingRef.current) {
                handleOnItemClick()
            }
            isDraggingRef.current = false
        }, [handleOnItemClick])

        const imageUrlsMap = useMemo(
            () =>
                images.map(image => ({
                    small: getImageUrl('small-mobile', image),
                    medium: getImageUrl('medium-mobile', image),
                    large: getImageUrl('large-mobile', image),
                    desktop: getImageUrl('desktop', image),
                    wideScreen: getImageUrl('wide-screen', image),
                })),
            [images],
        )

        // Matches MainImageComponent's getImageUrl() output so the browser can dedupe the request.
        const videoImageUrls = useMemo(
            () =>
                video
                    ? {
                          small: getImageUrl('small-mobile', video),
                          medium: getImageUrl('medium-mobile', video),
                          large: getImageUrl('large-mobile', video),
                          desktop: getImageUrl('desktop', video),
                          wideScreen: getImageUrl('wide-screen', video),
                      }
                    : null,
            [video],
        )

        const renderedImages = useMemo(
            () =>
                images.map((image, index) => {
                    const imageUrls = imageUrlsMap[index]

                    return (
                        <EmblaSlide key={`image_carousel_${index}`}>
                            <StyledImageContainer
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                imageBorder={imageBorder}
                                imageBorderRadius={imageBorderRadius}
                            >
                                <BgPictureContainer>
                                    {!!imageUrls.wideScreen && (
                                        <source media="(min-width:1024px)" srcSet={imageUrls.wideScreen} />
                                    )}
                                    {!!imageUrls.desktop && (
                                        <source media="(min-width:640px)" srcSet={imageUrls.desktop} />
                                    )}
                                    {!!imageUrls.large && <source media="(min-width:480px)" srcSet={imageUrls.large} />}
                                    {!!imageUrls.medium && (
                                        <source media="(min-width:320px)" srcSet={imageUrls.medium} />
                                    )}
                                    {!!imageUrls.small && (
                                        <StyledImage
                                            alt={image.caption}
                                            src={imageUrls.small}
                                            imageBorder={imageBorder}
                                            imageBorderRadius={imageBorderRadius}
                                            loading={!video && index < 1 ? 'eager' : 'lazy'}
                                            decoding={!video && index < 1 ? 'sync' : 'async'}
                                            fetchPriority={!video && index < 1 ? 'high' : 'low'}
                                        />
                                    )}
                                </BgPictureContainer>
                            </StyledImageContainer>
                        </EmblaSlide>
                    )
                }),
            [
                images,
                video,
                imageUrlsMap,
                handleMouseDown,
                handleMouseMove,
                handleMouseUp,
                handleMouseEnter,
                handleMouseLeave,
                imageBorder,
                imageBorderRadius,
            ],
        )

        return (
            <StyledCarouselWrapper>
                <EmblaViewport ref={emblaRef}>
                    <EmblaContainer>
                        {video && (
                            <EmblaSlide key="slide_video">
                                <StyledImageContainer
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                    imageBorder={imageBorder}
                                    imageBorderRadius={imageBorderRadius}
                                >
                                    <BgPictureContainer>
                                        {!!videoImageUrls?.wideScreen && (
                                            <source media="(min-width:1024px)" srcSet={videoImageUrls.wideScreen} />
                                        )}
                                        {!!videoImageUrls?.desktop && (
                                            <source media="(min-width:640px)" srcSet={videoImageUrls.desktop} />
                                        )}
                                        {!!videoImageUrls?.large && (
                                            <source media="(min-width:480px)" srcSet={videoImageUrls.large} />
                                        )}
                                        {!!videoImageUrls?.medium && (
                                            <source media="(min-width:320px)" srcSet={videoImageUrls.medium} />
                                        )}
                                        {!!videoImageUrls?.small && (
                                            <StyledImage
                                                alt={video.caption}
                                                src={videoImageUrls.small}
                                                imageBorder={imageBorder}
                                                imageBorderRadius={imageBorderRadius}
                                                loading="eager"
                                                decoding="sync"
                                                fetchPriority="high"
                                            />
                                        )}
                                    </BgPictureContainer>
                                    <VirtualTourWrapper>
                                        <VirtualTourTag
                                            analyticsCategory={pageTypeEventCategories.PROPERTY_DETAILS_PAGE}
                                            analyticsLabel={VirtualTourPosition.PDP_GALLERY}
                                        />
                                    </VirtualTourWrapper>
                                </StyledImageContainer>
                            </EmblaSlide>
                        )}
                        {renderedImages}
                    </EmblaContainer>
                </EmblaViewport>
                {!!showDots && (
                    <CarouselDots currentIndex={currentSlide} totalImages={video ? images.length + 1 : images.length} />
                )}
                {!!showArrows && (
                    <>
                        <LeftArrowContainer onClick={prev}>
                            <LeftNavigator />
                        </LeftArrowContainer>
                        <RightArrowContainer onClick={next}>
                            <RightNavigator />
                        </RightArrowContainer>
                    </>
                )}
                {!!showCountTile && (
                    <Counter>{`${currentSlide + 1} / ${video ? images.length + 1 : images.length}`}</Counter>
                )}

                <FullScreenModal
                    images={images}
                    video={video}
                    isFullScreenOpen={isFullScreen}
                    isOverlayOpen={isOverlayOpen}
                    setFullScreen={setFullScreen}
                    header={header}
                    footer={footer}
                    setOverlay={setOverlay}
                    setSelectedImage={goToSlide}
                    currentIndex={currentSlide}
                    onFullscreenImageClick={onFullscreenImageClick}
                    onFullScreenOpen={onFullScreenOpen}
                    shouldAutoplayVideo={shouldAutoplayVideo}
                />

                <OverlayModal
                    isOverlayOpen={isOverlayOpen}
                    setOverlay={setOverlay}
                    images={images}
                    video={video}
                    initialSlide={currentSlide}
                    setSelectedImage={goToSlide}
                    footer={footer}
                    onOverlayOpen={onOverlayOpen}
                    shouldAutoplayVideo={shouldAutoplayVideo}
                />
            </StyledCarouselWrapper>
        )
    },
)

GalleryCarousel.displayName = 'GalleryCarousel'

export default GalleryCarousel
